import React, { useState, useEffect } from "react";
import { Card, Breadcrumb, Button, Typography, Spin, message, Modal, Input, Select, Popconfirm } from "antd";
import { Link, useLocation, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";

const { Title, Text } = Typography;
const { Option } = Select;

const TicketDetails = () => {
  const { id } = useParams();
  const [ticket, setTickets] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isLoadingCancelBtn, setIsLoadingCancelBtn] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const location = useLocation();
  const isSupportRoute = location.pathname.includes("/admin/support");
  const filterOptions = ['Open', 'Work in progress', 'Canceled', 'Resolved'];
  const getData = async () => {
    try {
      const response = await axiosInstance.post(`/tickets/`, { ticket_id: id })
      setTickets(response.data.data);
      setNewStatus(response.data.data.status);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    try {
      setStatusUpdating(true);
      const response = await axiosInstance.post(`/tickets/${id}/update_status/`, { status: newStatus })
      message.success(response.data.message);
      setTickets((prev) => ({ ...prev, status: newStatus }));
    } catch (error) {
      message.error("Failed to update status");
      console.error("Error updating status:", error);
    } finally {
      setStatusUpdating(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleConfirmCancel = () => {
    handleCancelTicket(ticket.id, cancelReason); // Pass reason
    setIsModalVisible(false);
    setCancelReason(""); // Reset after cancel
  };

  const handleCancelTicket = async (id, cancel_message) => {
    try {
      setIsLoadingCancelBtn(true);
      const response = await axiosInstance.post(`/tickets/${id}/update_status/`, { cancel_message, status: 'Canceled' })
      // setTickets(response.data.data);
      message.success(response.data.message)
      setTickets((val) => {
        return { ...val, status: 'Canceled' }
      })
    } catch (error) {
      message.error(error.response.data.error)
      console.error("Error fetching tickets:", error);
    } finally {
      setIsLoadingCancelBtn(false);
    }
  };

  return (
    <>
      <div className="sticky -top-6 bg-white  dark:bg-[#17153B] z-10 shadow-sm p-4">
        <Breadcrumb className="text-lg font-medium text-gray-700">
          <Breadcrumb.Item>
            <Link to={isSupportRoute ? "/admin/support" : "/admin/settings"} className="hover:text-blue-600 transition-colors">
              {isSupportRoute ? 'Support Dashboard' : 'Tickets'}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="text-blue-500 font-semibold">Ticket Details</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-[#17153B]">
        {loading ? (
          <div className="flex items-center justify-center w-full h-screen bg-white dark:bg-[#17153B]">
            <Spin size="large" />
          </div>
        ) : (
          <div className="min-w-[600px] max-w-3xl mx-auto p-6 shadow-lg rounded-lg bg-white dark:bg-[#17153B] border border-gray-300 mt-4">
            <Title level={2} className="mb-4 text-gray-800 border-b pb-2">Ticket Details</Title>
            <Card className="p-6 border border-gray-300 shadow-sm bg-gray-50 dark:bg-[#17153B]">
              <div className="flex justify-between w-2/3 mx-auto items-center mb-4">
                <Text strong className="text-lg">Subject:</Text>
                <Text className="text-md block mb-2">{ticket.subject}</Text>
              </div>
              <div className="flex justify-between w-2/3 mx-auto items-center mb-4">
                <Text strong className="text-lg">Type:</Text>
                <Text className="text-md block mb-2">{ticket.subject_type}</Text>
              </div>
              <div className="flex justify-between w-2/3 mx-auto items-center mb-4">
                <Text strong className="text-lg">Status:</Text>
                <Text className="text-md block mb-2 text-green-600">{ticket.status}</Text>
              </div>
              <div className="flex justify-between w-2/3 mx-auto items-center mb-4">
                <Text strong className="text-lg">Description:</Text>
                <Text className="text-md block mb-2 text-black-600">{ticket.description}</Text>
              </div>
              {!isSupportRoute && <>
              <Select value={newStatus} onChange={setNewStatus} className="w-full mt-2">
                {filterOptions.map((option) => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Select>
                <Popconfirm
                  title={`Are you sure you want to change the status to "${newStatus}"?`}
                  onConfirm={handleStatusChange}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    loading={statusUpdating}
                    disabled={newStatus === ticket.status}
                    className="mt-2 w-full"
                  >
                    Update Status
                  </Button>
                </Popconfirm> </>}
            </Card>

            {ticket?.file && (
              <div className="mt-6 dark:bg-[#17153B] bg-white  p-4 border border-gray-300 rounded-lg shadow-sm">
                <Title level={4} className="mb-2 text-gray-800">Attachments</Title>
                <button
                  onClick={() => window.open(`${ticket.file}`, "_blank")}
                  className="text-blue-500 underline hover:text-blue-700"
                >
                  Open Attachment
                </button>
              </div>
            )}

            {isSupportRoute && <div className="mt-6 flex justify-end">
              <Button loading={isLoadingCancelBtn} disabled={ticket.status === 'Canceled' || isLoadingCancelBtn} type="primary" danger size="large" onClick={() => setIsModalVisible(true)}>
                Cancel Ticket
              </Button>
            </div>}
          </div>
        )}

        {/* Confirmation Modal */}
        <Modal
          title="Confirm Cancellation"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="no" onClick={() => setIsModalVisible(false)}>
              No
            </Button>,
            <Button key="yes" type="primary" danger onClick={handleConfirmCancel} disabled={!cancelReason.trim()}>
              Yes
            </Button>,
          ]}
        >
          <Text>Are you sure you want to cancel this ticket?</Text>
          <Input
            placeholder="Enter cancellation reason"
            className="mt-3"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </Modal>
      </div>
    </>
  );
};

export default TicketDetails;